import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import type { EmployeeDetail } from '../api/types';
import { useApp } from '../state/AppContext';
import { EmployeeName, Points, StatusPill, formatDate, formatLongDate, trim } from '../components/ui';

/**
 * One employee's rollup. Reached by drill-down, never from the nav.
 *
 * Every deduction is itemised, so the feed and the score always agree: an
 * employee with no in-period events shows an empty feed and a 100. This is
 * also the groundwork for the future employee self-service view.
 */
export function EmployeeDetailScreen() {
  const { employeeId } = useParams();
  const { periodId } = useApp();
  const { bandColor } = useApp();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['employee-detail', employeeId, periodId],
    queryFn: () => api.get<EmployeeDetail>(`/api/scorecard/${employeeId}`, { periodId }),
  });

  if (isLoading) return <div className="spinner-note">Loading employee…</div>;
  if (isError) return <div className="banner banner--error">{(error as Error).message}</div>;
  if (!data) return null;

  const band = bandColor(data.status);
  const rewardPct = data.rewardGuidance > 0 ? Math.min(100, (data.rewardPoints / data.rewardGuidance) * 100) : 0;

  return (
    <div>
      <button
        className="btn btn--sm"
        style={{ border: 'none', background: 'transparent', color: 'var(--blue)', padding: 0, marginBottom: 14, fontWeight: 600 }}
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 20 }}>
        <div>
          <div className="page-title">
            <EmployeeName employee={data.employee} />
          </div>
          <div className="page-subtitle">
            {data.employee.employeeId} · {data.employee.locationCode ?? 'no location'} ·{' '}
            {data.employee.department ?? 'no department'}
            {data.title ? ` · ${data.title}` : ''}
          </div>
        </div>
        <StatusPill label={data.status} color={band.color} bg={band.bg} />
      </div>

      <div className="stat-grid">
        <div className="stat">
          <div className="stat__label">CURRENT SCORE</div>
          <div className="stat__value" style={{ fontSize: 34, color: band.color }}>{trim(data.score)}</div>
          <div className="stat__note">
            Period: {data.periodName} ({formatLongDate(data.periodStart)} – {formatLongDate(data.periodEnd)})
          </div>
        </div>

        <div className="stat">
          <div className="stat__label">DEDUCTIONS THIS PERIOD</div>
          <div className="stat__value" style={{ fontSize: 34, color: 'var(--red)' }}>
            {data.deductions === 0 ? '0' : `-${trim(data.deductions)}`}
          </div>
          <div className="stat__note">
            {data.deductionCount === 0
              ? 'nothing counted yet'
              : `${data.deductionCount} item${data.deductionCount === 1 ? '' : 's'} counted`}
          </div>
          {/* Pending points are called out separately — they are real but they
              do not move the score until someone approves them. */}
          {data.pendingCount > 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--orange)', marginTop: 2 }}>
              {trim(data.pendingPoints)} pts across {data.pendingCount} pending flag
              {data.pendingCount === 1 ? '' : 's'} — not yet counted
            </div>
          )}
        </div>

        <div className="stat">
          <div className="stat__label">QUARTERLY REWARD POINTS</div>
          <div className="stat__value" style={{ fontSize: 34, color: 'var(--green)' }}>
            {trim(data.rewardPoints)}{' '}
            <span style={{ fontSize: 15, color: 'var(--muted)', fontWeight: 600 }} title="Guidance only — rewards are not capped by the system">/ {trim(data.rewardGuidance)} guide</span>
          </div>
          <div style={{ height: 6, background: 'var(--rule)', borderRadius: 4, marginTop: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${rewardPct}%`, background: 'var(--green)' }} />
          </div>
        </div>
      </div>

      <div className="section-label">ACTIVITY THIS PERIOD</div>
      <div className="card" style={{ padding: '6px 20px' }}>
        {data.feed.length === 0 && (
          <div style={{ padding: '16px 0', fontSize: 12.5, color: 'var(--muted)' }}>
            No scored events in {data.periodName} — the score of {trim(data.score)} is the period's clean starting score.
          </div>
        )}

        {data.feed.map((f, i) => {
          // Only settled events move the score. Pending and rejected ones stay
          // in the feed with their points intact — the number is the whole point
          // of the row — but each carries an explicit badge saying why it did not
          // count, so the feed can never be read as disagreeing with the score.
          //
          // Wording matters here: a rejected flag means the employee gave a reason
          // and it was accepted, so this row is in their favour. Labelling it
          // "REJECTED" on their own page states the reviewer's verb and reads as a
          // black mark for the opposite of what happened. Say what it meant for
          // them — no deduction — and keep the reviewer's vocabulary on the review
          // screen where the decision is actually being made.
          const counts = f.status === 'settled';
          const badge =
            f.status === 'pending'
              ? { text: 'PENDING', color: 'var(--orange)' }
              : f.status === 'not counted'
                ? { text: 'NO DEDUCTION', color: 'var(--muted)' }
                : null;
          return (
            <div
              key={`${f.date}-${i}`}
              style={{ display: 'flex', gap: 14, padding: '13px 0', borderBottom: '1px solid var(--rule)' }}
            >
              <div style={{ width: 74, flex: 'none', fontSize: 11.5, color: 'var(--muted)', paddingTop: 1 }}>
                {formatDate(f.date)}
              </div>
              {/* Severity by point magnitude, so the eye finds the −10s first.
                  Anything not counted goes grey — colour is reserved for points
                  that are actually on the score. */}
              <div
                style={{
                  width: 8, height: 8, borderRadius: '50%', marginTop: 5, flex: 'none',
                  background: !counts
                    ? 'var(--rule)'
                    : f.points > 0 ? 'var(--green)' : f.points <= -5 ? 'var(--red)' : 'var(--orange)',
                }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>{f.label}</span>
                  {badge && (
                    <span
                      style={{
                        fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5, padding: '1px 5px',
                        borderRadius: 3, color: badge.color, border: `1px solid ${badge.color}`,
                      }}
                    >
                      {badge.text}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  {f.source}
                  {f.status === 'pending' && ' · pending review — no points taken yet'}
                  {f.status === 'not counted' && ' · reason accepted on review — no points taken'}
                </div>
              </div>
              {/* The points stay legible even when they do not count: hiding or
                  striking the number would answer "why is this here?" by taking
                  away the thing the reader came to check. Grey, not red, carries
                  the "does not apply" instead. */}
              <div style={{ textAlign: 'right' }}>
                {counts ? (
                  <Points value={f.points} />
                ) : (
                  <span style={{ fontWeight: 700, color: 'var(--muted)' }}>{trim(f.points)}</span>
                )}
                {!counts && (
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.3, color: 'var(--muted)' }}>
                    {f.status === 'pending' ? 'NOT YET TAKEN' : 'WAIVED'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
